import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import './AdminProductDetailPage.css';

// Helper function to get product image from image field or images array
const getProductImage = (product) => {
  if (!product) return null;
  
  // Check images array first (could be string or array) - this has the actual uploaded images
  if (product.images) {
    try {
      const imagesArray = typeof product.images === 'string' 
        ? JSON.parse(product.images) 
        : product.images;
      if (Array.isArray(imagesArray) && imagesArray.length > 0 && imagesArray[0]) {
        return imagesArray[0];
      }
    } catch (e) {
      console.error('Error parsing product images:', e);
    }
  }
  
  // Then check main image field - but skip invalid URLs
  if (product.image) {
    const img = product.image;
    // Skip placeholder or invalid URLs (like Pexels page URLs, not image URLs)
    if (img.includes('da821e6a6c344d588c41324f62b0dda825ecff92') || 
        img.includes('via.placeholder.com') ||
        img.includes('pexels.com/photo/') ||
        (!img.match(/\.(jpg|jpeg|png|gif|webp|svg)/i) && !img.includes('unsplash.com') && !img.includes('localhost'))) {
      return null;
    }
    return img;
  }
  
  return null;
};

const AdminProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await productsApi.getById(id);
        console.log('Product loaded:', data.id, 'Status:', data.status);
        setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    console.log('=== Button clicked ===');
    console.log('handleStatusChange called with:', newStatus, 'for product:', product?.id);
    
    if (!product || !product.id) {
      alert('Error: Product not found');
      return;
    }
    
    setStatusUpdating(true);
    try {
      let result;
      if (newStatus === 'approved') {
        result = await productsApi.approve(product.id);
      } else if (newStatus === 'rejected') {
        result = await productsApi.reject(product.id);
      } else if (newStatus === 'published') {
        result = await productsApi.publish(product.id);
      } else {
        result = await productsApi.updateStatus(product.id, newStatus);
      }
      console.log('API response:', result);
      setProduct(prev => ({ ...prev, status: newStatus }));
      alert(`Product ${newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : newStatus === 'published' ? 'published' : 'status updated'} successfully!`);
      if (newStatus === 'rejected') {
        navigate('/admin/products');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update product status: ' + (err.response?.data?.error || err.message));
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="apd-loading">
          <div className="apd-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !product) {
    return (
      <AdminLayout>
        <div className="apd-error">
          <p>{error || 'Product not found'}</p>
          <button onClick={() => navigate('/admin/products')}>Back to Products</button>
        </div>
      </AdminLayout>
    );
  }

  const seller = product.seller || {};

  // Parse key features from description or use defaults
  const keyFeatures = [
    { label: 'High Strength', value: '750 - 950 PSI' },
    { label: 'Weight', value: '12 kg per unit' },
    { label: 'Dimension', value: '600mm x 400mm x 50mm' },
    { label: 'Corrosion Resistance', value: 'Zinc coating, aluminum plating, Stainless Steel' },
    { label: 'Model Number', value: product.modelNumber || 'N/A' },
    { label: 'Unit', value: product.unit || 'Piece' },
    { label: 'MOQ', value: product.moq || 'N/A' },
  ];

  const recommendedUse = [
    'Coastal and Marine',
    'Suitable for high humidity',
    'Corrosion-resistant applications',
  ];

  const sellerNotes = [
    'Our high-quality scaffolding materials, designed for single and double-walled construction, can be used in industrial applications (500 °C). Built to withstand harsh conditions (weathering, cracking, loads, sunlight), they are non-conductive and lightweight. Suitable for petrochemical refinery, Dross, Degassing & Die Attach, Magnesium Mold/Aluminum Cores, and Non-corrosive/non-reactive to chemical attacks.',
    '2-ply reinforced, with 4-layers, using multiple wall- and high-performance ratings. Chemical resistance in a variety of applications for a solvent.',
    '3-ply bonded, with 4-layers, using chemical bonds and high-performance ratings. Long lifespan and environment-friendly products.',
  ];

  return (
    <AdminLayout>
      <div className="apd-container">
        {/* Modal/Card Style Header */}
        <div className="apd-modal-card">
          {/* Company Header Row */}
          <div className="apd-company-row">
            <div className="apd-company-left">
              <img 
                src={seller.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100'} 
                alt={seller.name}
                className="apd-company-avatar"
              />
              <div className="apd-company-details">
                <h2 className="apd-company-name">{seller.name || 'Gulf Building Supplies LLC 2'}</h2>
                <div className="apd-company-meta">
                  {seller.isVerified && (
                    <span className="apd-verified-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="22 4 12 14.01 9 11.01" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Verified
                    </span>
                  )}
                  <span className="apd-country-badge">{seller.country || 'United Arab Emirates'}</span>
                </div>
                <p className="apd-reviews-products">0 Reviews | {seller._count?.products || 97} Products</p>
              </div>
            </div>
            <button className="apd-close-btn" onClick={() => navigate('/admin/products')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Product Image */}
          <div className="apd-product-image-container">
            <img 
              src={getProductImage(product) || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'} 
              alt={product.name}
              className="apd-product-image"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'; }}
            />
          </div>
        </div>

        {/* Details Card */}
        <div className="apd-details-card">
          {/* Title Section */}
          <div className="apd-title-section">
            <h1 className="apd-product-title">{seller.name || 'Gulf Building Supplies LLC 2'}</h1>
            <p className="apd-product-subtitle">A Leading Construction Supplier with 15+ Years of Industry Experience</p>
            <p className="apd-product-description">{product.description || 'Fiberglass composite scaffold plank. Lightweight and non-conductive.'}</p>
            <a href="#" className="apd-company-link">Company Link</a>
          </div>

          {/* Certifications Section */}
          <div className="apd-section">
            <h3 className="apd-section-title">Certifications & Accreditations</h3>
            <p className="apd-section-text">An open statement for a trading marketplace and detailed web design studio content. Sed vitae...</p>
          </div>

          {/* Basic Information */}
          <div className="apd-section">
            <h3 className="apd-section-title">Basic Information</h3>
            <div className="apd-info-grid">
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">BUSINESS TYPE</span>
                  <span className="apd-info-value">{seller.businessType || 'Manufacturer'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">LICENSE NUMBER</span>
                  <span className="apd-info-value">{seller.licenseNumber || 'N/A'}</span>
                </div>
              </div>
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">SECTOR OF EXPERTISE</span>
                  <span className="apd-info-value">{product.category?.name || 'Scaffolding & Shuttering'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">COMPANY</span>
                  <span className="apd-info-value">{seller.name || 'Gulf Building Supplies LLC 2'}</span>
                </div>
              </div>
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">CORE OFFERINGS</span>
                  <span className="apd-info-value">{product.name || 'Composite Plank System'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">YEAR OF ESTABLISHMENT</span>
                  <span className="apd-info-value">{seller.establishYear || '2010'}</span>
                </div>
              </div>
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">TOTAL EMPLOYEE</span>
                  <span className="apd-info-value">{seller.numberOfEmployees || '50-100'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">LOCATION SERVICES</span>
                  <span className="apd-info-value">{seller.serviceArea || 'UAE, Saudi Arabia, Qatar'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="apd-section">
            <h3 className="apd-section-title">Contact Information</h3>
            <div className="apd-info-grid">
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">TELEPHONE</span>
                  <span className="apd-info-value">{seller.phoneNumber || '+971 XX XXX XXXX'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">ADDRESS</span>
                  <span className="apd-info-value">{seller.address || 'Dubai, UAE'}</span>
                </div>
              </div>
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">PO BOX</span>
                  <span className="apd-info-value">{seller.zipCode || 'N/A'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">COUNTRY</span>
                  <span className="apd-info-value">{seller.country || 'United Arab Emirates'}</span>
                </div>
              </div>
              <div className="apd-info-row">
                <div className="apd-info-item">
                  <span className="apd-info-label">CITY</span>
                  <span className="apd-info-value">{seller.city || 'Dubai'}</span>
                </div>
                <div className="apd-info-item">
                  <span className="apd-info-label">FAX</span>
                  <span className="apd-info-value">N/A</span>
                </div>
              </div>
            </div>
          </div>

          {/* Product Overview */}
          <div className="apd-section">
            <h3 className="apd-section-title">Product Overview</h3>
            <p className="apd-section-text">
              {product.description || 'Completed at the forty-container, contractor-grade double by simple rubber sleeve with is combined, heavy industrial traffic, Living Room Floor with soft-soap, natural cut design, Granite with 6"/100mm floor mounting. Rated for a maximum tile thickness of up to 6" mm, it is available with eco-friendly tile-backing. Heat and weather resistant. Rated for 300 degrees Fahrenheit.'}
            </p>
            <p className="apd-section-text" style={{marginTop: '12px'}}>
              Modern wax buildup formula produced in a single mold die method, automatic, smooth flow, liquid texture & an high protection rating. Fully functional with a dynamic style type system.
            </p>
          </div>

          {/* Key Features */}
          <div className="apd-section">
            <h3 className="apd-section-title">Key Features</h3>
            <div className="apd-features-list">
              {keyFeatures.map((feature, index) => (
                <div key={index} className="apd-feature-item">
                  <span className="apd-feature-label">{feature.label}</span>
                  <span className="apd-feature-value">{feature.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Use */}
          <div className="apd-section">
            <h3 className="apd-section-title">Recommended Use</h3>
            <ul className="apd-recommended-list">
              {recommendedUse.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          {/* Seller's Notes */}
          <div className="apd-section">
            <h3 className="apd-section-title">Seller's Notes</h3>
            <div className="apd-seller-notes">
              {sellerNotes.map((note, index) => (
                <p key={index} className="apd-note-item">
                  <span className="apd-note-number">{index + 1}.</span> {note}
                </p>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="apd-actions">
            {/* Only show Reject button if product is not already rejected */}
            {product.status?.toLowerCase() !== 'rejected' && (
              <button 
                type="button"
                className="apd-reject-btn" 
                disabled={statusUpdating}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusChange('rejected');
                }}
              >
                {statusUpdating ? 'Processing...' : 'Reject'}
              </button>
            )}
            {/* Show Approved (disabled) if product is already approved/published, otherwise show Approve button */}
            {['approved', 'published'].includes(product.status?.toLowerCase()) ? (
              <button type="button" className="apd-approved-btn" disabled>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Approved
              </button>
            ) : (
              <button 
                type="button"
                className="apd-approve-btn" 
                disabled={statusUpdating}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusChange('approved');
                }}
              >
                {statusUpdating ? 'Approving...' : 'Approve'}
              </button>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProductDetailPage;
